import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AnalysisApiClientAccess } from '@nk/access-block';
import { CachingService, StableCache } from '@nk/caching-lib';
import { EntityUpdateTimestampService } from '@nk/core/services/entity-update-timestamp.service';
import {
  AppFeatureKey,
  AppRoleKey,
  CompanyCollateralAggregateEntity,
  CompanyCollateralComposite,
  CompanyCollateralCreditorAggregateEntity,
  CompanyCollateralCreditorCountryAggregateEntity,
  CompanyCollateralCreditorEntity,
  CompanyCollateralEntity,
  CompanyCollateralTypeEntity,
  COUNTRY_FEATURES,
  SimpleCollection,
  WithUuid,
} from '@nk/data-entities-lib';
import { isNumber } from '@nk/utils-lib';
import { Observable } from 'rxjs';

@Injectable()
export class CollateralService {
  constructor(
    private _analysisApiAccess: AnalysisApiClientAccess,
    private _cachingService: CachingService,
    private _entityUpdateTimestampService: EntityUpdateTimestampService,
  ) {}

  public companyCollateralListByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralByCompanyList,
  );
  public fetchCompanyCollateralByCompanyList(
    companyUuid: string,
    showForCorporateGroup: boolean | null,
  ): Observable<CompanyCollateralEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('current_flag', false);
    if (showForCorporateGroup !== null) {
      httpParams = httpParams.append('include_corporate_group_flag', showForCorporateGroup); // TODO: better name?? maybe include corporate group
    }
    return this._analysisApiAccess.get<CompanyCollateralEntity[]>(
      `/company/${companyUuid}/company-collateral`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralCompositeCollection = this._cachingService.create(
    this,
    this.fetchCompanyCollateralCompositeCollection,
  );
  public fetchCompanyCollateralCompositeCollection(
    companyIdentifier: string,
    showForCorporateGroup: boolean | null,
    includeDeletedCollateral: boolean | null,
    limit: number,
    offset: number,
  ): Observable<SimpleCollection<CompanyCollateralComposite>> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('company_identifier', companyIdentifier);
    httpParams = httpParams.append('limit', limit);
    httpParams = httpParams.append('offset', offset);

    if (showForCorporateGroup !== null) {
      httpParams = httpParams.append('include_corporate_group_flag', showForCorporateGroup); // TODO: better name?? maybe include corporate group
    }
    if (includeDeletedCollateral !== null) {
      httpParams = httpParams.append('current_flag', includeDeletedCollateral); // TODO: better name?? maybe include corporate group
    }
    return this._analysisApiAccess.get<SimpleCollection<CompanyCollateralComposite>>(
      `/company-collateral-composite-collection`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralTypeListCache = new StableCache(
    'app.company_collateral_type',
    this,
    this.fetchCompanyCollateralTypeList,
    this._entityUpdateTimestampService.timestamps$,
  );
  public fetchCompanyCollateralTypeList(): Observable<CompanyCollateralTypeEntity[]> {
    return this._analysisApiAccess.get<CompanyCollateralTypeEntity[]>(`/company-collateral-type`, {});
  }

  public companyCollateralCreditorListCache = new StableCache(
    'app.company_collateral_creditor',
    this,
    this.fetchCompanyCollateralCreditorList,
    this._entityUpdateTimestampService.timestamps$,
  ); // TODO: delete me. Fetching all creditors takes around 20Mb and significant amount of time. I propose that we get rid of this, and download only the ones we need.
  public companyCollateralCreditorsByUuidsCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralCreditorList,
  );
  public fetchCompanyCollateralCreditorList(
    companyCollateralCreditorUuids?: string[],
  ): Observable<WithUuid<CompanyCollateralCreditorEntity>[]> {
    let httpParams = new HttpParams();
    if (Array.isArray(companyCollateralCreditorUuids)) {
      httpParams = httpParams.append('company_collateral_creditor_uuids', companyCollateralCreditorUuids.join(','));
    }
    return this._analysisApiAccess.get<WithUuid<CompanyCollateralCreditorEntity>[]>(`/company-collateral-creditor`, {
      params: httpParams,
    });
  }

  public companyCollateralCreditorCollectionCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralCreditorCollection,
  );
  public fetchCompanyCollateralCreditorCollection(
    searchQuery?: string,
    limit?: number,
    offset?: number,
  ): Observable<SimpleCollection<WithUuid<CompanyCollateralCreditorEntity>>> {
    let httpParams = new HttpParams();
    if (searchQuery) {
      httpParams = httpParams.append('query', searchQuery);
    }
    if (isNumber(limit)) {
      httpParams = httpParams.append('limit', limit);
    }
    if (isNumber(offset)) {
      httpParams = httpParams.append('offset', offset);
    }
    return this._analysisApiAccess.get<SimpleCollection<WithUuid<CompanyCollateralCreditorEntity>>>(
      `/company-collateral-creditor-collection`,
      {
        params: httpParams,
      },
    );
  }

  public companyCollateralAggregateByCompanyCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralAggregateByCompany,
  );
  public fetchCompanyCollateralAggregateByCompany(
    companyUuid: string,
    includeCorporateGroup = false,
    quarterlyDataPoints = false,
    collateralTypeUuid: string | null,
    fromDate: Date | null,
  ): Observable<CompanyCollateralAggregateEntity[]> {
    let httpParams = new HttpParams();
    httpParams = httpParams.append('include_corporate_group_flag', includeCorporateGroup);
    httpParams = httpParams.append('quarterly_date_flag', quarterlyDataPoints);
    if (collateralTypeUuid) {
      httpParams = httpParams.append('company_collateral_type_uuid', collateralTypeUuid);
    }
    if (fromDate) {
      httpParams = httpParams.append('from_snapshot_date', fromDate.toISOString());
    }
    return this._analysisApiAccess.get<CompanyCollateralAggregateEntity[]>(
      `/company/${companyUuid}/company-collateral-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralAggregateByCountry = this._cachingService.create(
    this,
    this.fetchCompanyCollateralAggregateByCountry,
  );
  public fetchCompanyCollateralAggregateByCountry(
    geoCountryUuid: string,
    collateralTypeKey: string,
    latestFlag: boolean = null,
    numberOfCreditors: number = null,
  ): Observable<CompanyCollateralCreditorCountryAggregateEntity[]> {
    let httpParams = new HttpParams();
    if (latestFlag !== null) {
      httpParams = httpParams.append('latest_flag', String(latestFlag));
    }
    if (numberOfCreditors !== null) {
      httpParams = httpParams.append('number_of_creditors', String(numberOfCreditors));
    }
    return this._analysisApiAccess.get<CompanyCollateralCreditorCountryAggregateEntity[]>(
      `/geo-country/${geoCountryUuid}/company-collateral-type/${collateralTypeKey}/company-collateral-creditor-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralAggregateBySubdivision = this._cachingService.create(
    this,
    this.fetchCompanyCollateralAggregateBySubdivison,
  );
  public fetchCompanyCollateralAggregateBySubdivison(
    geoSubdivisionUuid: string,
    collateralTypeUuid: string,
    latestFlag: boolean = null,
    numberOfCreditors: number = null,
  ): Observable<CompanyCollateralCreditorAggregateEntity[]> {
    let httpParams = new HttpParams();
    if (latestFlag !== null) {
      httpParams = httpParams.append('latest_flag', String(latestFlag));
    }
    if (numberOfCreditors !== null) {
      httpParams = httpParams.append('number_of_creditors', String(numberOfCreditors));
    }
    return this._analysisApiAccess.get<CompanyCollateralCreditorAggregateEntity[]>(
      `/geo-subdivision/${geoSubdivisionUuid}/company-collateral-type/${collateralTypeUuid}/company-collateral-creditor-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralAggregateByCreditorAndTypeCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralAggregateByCreditorAndType,
  );
  public fetchCompanyCollateralAggregateByCreditorAndType(
    creditorUuid: string,
    typeUuid: string,
    latestFlag: boolean,
  ): Observable<CompanyCollateralCreditorAggregateEntity[]> {
    let httpParams = new HttpParams();
    if (latestFlag !== null) {
      httpParams = httpParams.append('latest_flag', String(latestFlag));
    }
    return this._analysisApiAccess.get<CompanyCollateralCreditorAggregateEntity[]>(
      `/company-collateral-creditor/${creditorUuid}/company-collateral-type/${typeUuid}/company-collateral-creditor-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data],
      [AppRoleKey.norwegian_property_data],
    );
  }

  public companyCollateralSubdivisionAggregateBySubdivisionAndTypeCache = this._cachingService.create(
    this,
    this.fetchCompanyCollateralSubdivisionAggregateByCreditorAndType,
  );
  public fetchCompanyCollateralSubdivisionAggregateByCreditorAndType(
    geoSubdivisionUuid: string,
    collateralTypeUuid: string,
    latestFlag: boolean,
  ): Observable<CompanyCollateralCreditorAggregateEntity[]> {
    let httpParams = new HttpParams();
    if (latestFlag) {
      httpParams = httpParams.append('latest_flag', String(latestFlag));
    }
    return this._analysisApiAccess.get<CompanyCollateralCreditorAggregateEntity[]>(
      `/geo-subdivision/${geoSubdivisionUuid}/company-collateral-type/${collateralTypeUuid}/company-collateral-subdivision-aggregate`,
      { params: httpParams },
      [AppFeatureKey.base, AppFeatureKey.collateral, AppFeatureKey.norwegian_property_data, ...COUNTRY_FEATURES],
      [AppRoleKey.norwegian_property_data],
    );
  }
}
